// ProfileTab.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import { ProfileListType } from '../resource/profileConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color, headerHeight, breakpoint } from '../style/variables.js';
import media from '../style/media.js';

const tabObject = {
  [ProfileListType.HOME]: {
    titleI18nKey: 'home_page',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileHome,
  },
  [ProfileListType.STORY]: {
    titleI18nKey: 'broadcast',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileStory,
  },
  [ProfileListType.SHORT]: {
    titleI18nKey: 'profile_shorts',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileShorts,
  },
  [ProfileListType.FLIX]: {
    titleI18nKey: 'title_flix',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileVideo,
  },
  [ProfileListType.IMAGE]: {
    titleI18nKey: 'profile_photo',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileImage,
  },
  [ProfileListType.LIVESTREAM_SESSION]: {
    titleI18nKey: 'LIVE',
    mixpanelButtonId: ButtonId.Profile.ButtonProfileLive,
  },
};

const tabStyle = {
  marginRight: '24px',
  flex: 'none',
};
const lastTabStyle = {
  ...tabStyle,
  marginRight: '0px',
};
const tabLabelStyle = {
  margin: '0px',
  opacity: 1,
};

export const ProfileTab = ({
  userId,
  deviceWidth,
  profileTabs,
  isMeProfile,
  listType,
  t,
  replace,
}) => {
  const tabsList = profileTabs;
  const tabs = tabsList
    .filter(tabId =>
      isMeProfile ? true : ProfileListType.LIVESTREAM_SESSION !== tabId
    )
    .filter(tabId => tabId);

  const handleTabChange = useCallback(
    (event, value) => {
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: tabObject[value]?.mixpanelButtonId,
        },
      });
      const url = new URL(location.href);
      ['feed', '_feed', 'filters', '_filters', 'sorts', '_sorts'].forEach(key =>
        url.searchParams.delete(key)
      );
      if (tabs[0] === value) {
        return replace({
          pathname: `${`/user/${userId}`}${url.search}`,
        });
      }
      return replace({
        pathname: `${`/user/${userId}`}/${value}${url.search}`,
      });
    },
    [userId, tabs, replace]
  );
  const isOnMobile = deviceWidth <= breakpoint.mobile;
  return (
    <StyledProfileTab>
      <Tabs
        value={listType}
        onChange={handleTabChange}
        isScrollable={isOnMobile}
      >
        {tabs.map((tabId, index) => {
          const isActive = tabId === listType;
          const i18nKey = tabObject[tabId]?.titleI18nKey;
          const title =
            t(i18nKey, {
              ns: TranslationNamespace.PROFILE,
            }) || i18nKey;
          return (
            <Tab
              key={tabId}
              value={tabId}
              style={
                isOnMobile
                  ? tabs.length - 1 === index
                    ? lastTabStyle
                    : tabStyle
                  : undefined
              }
              labelStyle={tabLabelStyle}
              label={<TabText isActive={isActive}>{title}</TabText>}
            />
          );
        })}
      </Tabs>
    </StyledProfileTab>
  );
};

ProfileTab.propTypes = {
  userId: PropTypes.string,
  listType: PropTypes.string,
  deviceWidth: PropTypes.number,
  isMeProfile: PropTypes.bool,
  profileTabs: PropTypes.array,
  t: PropTypes.func.isRequired,
  replace: PropTypes.func,
};

ProfileTab.defaultProps = {
  userId: null,
  listType: null,
  deviceWidth: 0,
  isMeProfile: false,
  profileTabs: [],
  replace: () => null,
};

const StyledProfileTab = styled.div`
  position: sticky;
  top: ${headerHeight}px;
  left: 0px;
  right: 0px;
  z-index: 2;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  background-color: ${color.black};
`;

const TabText = styled.h2`
  font-size: 16px;
  margin: 0;
  font-weight: normal;
  ${media.mobile`
    font-size: 14px;
  `};
`;

export default withTranslation()(ProfileTab);
